import React, { useEffect, useState, useRef, useContext } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled, { ThemeProvider } from "styled-components/macro";
import { use100vh } from "react-div-100vh";
import { get } from "lodash";
import { LayoutItem } from "simple-keyboard-layouts/build/interfaces";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import KeyboardLayouts from "simple-keyboard-layouts/build/index";
import mediaBreakpoints from "./utilities/media-sc-breakpoints";
import { BreakpointProvider } from "./utilities/media-js-breakpoints";
import ISO from "./utilities/languageISOConverter";
import { KeyboardContext } from "./utilities/virtual-keyboard";
import Configurator from "./pages/Configurator";
import Summary from "./pages/Summary";
import ModelSelectorPage from "./pages/ModelSelector";
import ErrorAlert from "./components/Alerts/ErrorAlert";
import { AppLoader } from "./components/Loader/AppLoader";
import { RESOLUTION_CHANGED } from "./store/actions/imageActions";
import { REGISTER_ACTIVITY } from "./store/actions/timeoutActions";
import {
    getAppConfig,
    getConfigurationString,
    getFrontendSettings,
    getIsLoading,
    getShowVirtualKeyboard,
    getLanguage,
    getTouchScreen,
} from "./store/sagas/selectors";
import Constants from "./constants";
import { buildUrlStateSearch } from "./utilities/navigationUtils";
import { setNavigate } from "./utilities/navigationService";
import { SessionInitFromUrl } from "./components/SessionInitFromUrl";

const AppContainer = styled.div`
    font-family: ${props => get(props.theme, "fontFamily", "sans-serif")};
    color: ${props => get(props.theme, "textColor", "inherit")};
    background: ${props => get(props.theme, "background.color", "#fff")};
`;

const VirtualKeyboard = styled.div`
    width: 40%;
    ${({ theme }) => theme.bp.xxxl`
        width: 20%;
        transform: scale(2, 2);
        transform-origin: bottom center;
    `};
`;

const VirtualKeyboardContainer = styled.div`
    position: absolute;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    bottom: 0;
    z-index: 10000;
`;

const App = () => {
    const maxHeight: number | null = use100vh();

    const dispatch = useDispatch();

    const isLoading = useSelector(getIsLoading);
    const frontendSettings = useSelector(getFrontendSettings);
    const appConfig = useSelector(getAppConfig);
    const configurationString = useSelector(getConfigurationString);
    const showVirtualKeyboard = useSelector(getShowVirtualKeyboard);
    const language = useSelector(getLanguage);
    const isTouchScreen = useSelector(getTouchScreen);
    const [keyboardLayoutName, setkeyboardLayoutName] = useState("default");

    let languageISOValue = "english";
    if (language) {
        languageISOValue = ISO[language.toLocaleLowerCase()] || "english";
    }

    const layout = new KeyboardLayouts().get(languageISOValue) as LayoutItem;

    const [config, setConfig] = useState<any>({ bp: mediaBreakpoints });

    const VirtualKeyboardContext = useContext(KeyboardContext);
    const virtualKeyboardRef = useRef<any>(null);

    const resetTimer = () => {
        dispatch({ type: REGISTER_ACTIVITY });
    };
    document.onclick = resetTimer;
    document.onkeydown = resetTimer;
    document.ontouchstart = resetTimer;
    document.onscroll = resetTimer;

    const setStyleProperties = (configProperties: any) => {
        const r = document.documentElement;
        r.style.setProperty("--primary-color", configProperties.colors.primaryColor);
        r.style.setProperty("--primary-hover-color", configProperties.colors.primaryHoverColor);
        r.style.setProperty("--primary-active-color", configProperties.colors.primaryActiveColor);
        r.style.setProperty("--light-hover-color", configProperties.colors.lightHoverColor);
        r.style.setProperty("--light-active-color", configProperties.colors.lightActiveColor);
    };

    useEffect(() => {
        const preventDefault = (event: Event) => event.preventDefault();

        if (isTouchScreen) {
            document.addEventListener("contextmenu", preventDefault);
        }

        return () => {
            if (isTouchScreen) {
                document.removeEventListener("contextmenu", preventDefault);
            }
        };
    }, [isTouchScreen]);

    useEffect(() => {
        const handleResize = () => {
            dispatch({ type: RESOLUTION_CHANGED });
        };
        window.addEventListener("resize", handleResize);

        const handleMessage = (event: MessageEvent) => {
            if (event.data.type) dispatch(event.data);
        };
        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("message", handleMessage);
        };
    }, [dispatch]);

    useEffect(() => {
        if (!appConfig) return;

        const breakpoints = { bp: mediaBreakpoints };
        setConfig({ ...appConfig.style, ...breakpoints });
        setStyleProperties(appConfig);
    }, [appConfig]);

    const updateConfigStringParam = (configString: string | null) => {
        const search = buildUrlStateSearch(window.location.search, configString);
        window.history.pushState(null, "", `${window.location.pathname}${search}`);
    };

    // Remove config_string parameter from URL when enable_url_configuration_state is false.
    useEffect(() => {
        if (frontendSettings && !frontendSettings.enable_url_configuration_state) {
            updateConfigStringParam(null);
        }
    }, [frontendSettings]);

    // Update config_string parameter in URL when enable_url_configuration_state is true.
    useEffect(() => {
        if (configurationString && frontendSettings?.enable_url_configuration_state) {
            updateConfigStringParam(configurationString);
        }
    }, [configurationString, frontendSettings]);

    const navigate = useNavigate();
    useEffect(() => {
        setNavigate(navigate);
        return () => setNavigate(null);
    }, [navigate]);

    const setKeyboardRef = (ref: any, startValue: string) => {
        virtualKeyboardRef.current = ref;
        virtualKeyboardRef.current.setInput(startValue);
    };

    const onKeyPress = (button: string) => {
        if (button === "{shift}" || button === "{lock}") {
            setkeyboardLayoutName(keyboardLayoutName === "default" ? "shift" : "default");
        }
    };

    const drawVirtualKeyboard = () => {
        return (
            <VirtualKeyboardContainer>
                <VirtualKeyboard>
                    <KeyboardContext.Provider value={VirtualKeyboardContext}>
                        {showVirtualKeyboard ? (
                            <Keyboard
                                keyboardRef={r => setKeyboardRef(r, VirtualKeyboardContext.content)}
                                layout={layout.layout}
                                layoutName={keyboardLayoutName}
                                onKeyPress={onKeyPress}
                                preventMouseDownDefault
                                useMouseEvents
                                onChange={(input: string) => {
                                    if (VirtualKeyboardContext.listener) {
                                        VirtualKeyboardContext.listener(input);
                                    }
                                }}
                            />
                        ) : null}
                    </KeyboardContext.Provider>
                </VirtualKeyboard>
            </VirtualKeyboardContainer>
        );
    };

    return (
        <BreakpointProvider>
            <ThemeProvider theme={config}>
                {/* SessionInitFromUrl runs before Routes so it can dispatch SET_IS_LOADING / CREATE_SESSION without mounting route components; no race with router context. */}
                <SessionInitFromUrl />
                {isLoading ? (
                    <AppLoader
                        color={get(config, "appLoader.color", "#888")}
                        background={get(config, "appLoader.backgroundColor")}
                        height={200}
                        width={80}
                    />
                ) : (
                    <AppContainer style={{ height: maxHeight || "100vh" }}>
                        <Routes>
                            <Route path={Constants.modelSelectorUrlPath} element={<ModelSelectorPage />} />
                            <Route path={Constants.summaryUrlPath} element={<Summary />} />
                            <Route path={Constants.configuratorUrlPath} element={<Configurator />} />
                            <Route path="/" element={<Configurator />} />
                        </Routes>
                        {drawVirtualKeyboard()}
                    </AppContainer>
                )}
                <ErrorAlert />
            </ThemeProvider>
        </BreakpointProvider>
    );
};

export default App;
